import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import { NAV, SITE } from "@/data/content";
import { EASE } from "./Reveal";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          solid ? "glass-light shadow-card" : "bg-transparent border-b border-white/10"
        }`}
      >
        <div className="container-luxe h-20 flex items-center justify-between">
          <Logo dark={!solid ? true : false} />
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`navbar-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[13px] font-semibold tracking-wide transition-colors duration-300 hover:text-gold-dark ${
                  solid ? "text-navy" : "text-white/85"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className="hidden lg:block">
            <a href="#contact" data-testid="navbar-cta-enquire" className="btn-gold !px-6 !py-2.5">
              Enquire Now
            </a>
          </div>
          <button
            className={`lg:hidden p-2 ${solid ? "text-navy" : "text-white"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="navbar-mobile-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-navy pt-24 px-6 pb-10 flex flex-col overflow-y-auto"
            initial={{ opacity: 0, y: -24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.4, ease: EASE }}
            data-testid="navbar-mobile-menu"
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV.map((item, i) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-3xl text-white py-2.5 border-b border-white/10 hover:text-gold transition-colors duration-300"
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                >
                  {item.label}
                </motion.a>
              ))}
            </nav>
            <div className="mt-auto flex flex-col gap-3 pt-8">
              <a href={SITE.phoneHref} data-testid="mobile-call-button" className="btn-gold w-full">
                <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
              </a>
              <p className="text-center font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">
                {SITE.location}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
